/**
 * Service pour la gestion des catégories de produits.
 *
 * Fournit des méthodes pour :
 * - createCategory : créer une nouvelle catégorie.
 * - getCategory : récupérer la liste des catégories avec pagination.
 * - getCategoryById : récupérer une catégorie spécifique par son identifiant.
 * - updateCategoryById : mettre à jour une catégorie existante.
 * - DeleteCategory : supprimer une catégorie existante.
 *
 * Utilise axiosInstance pour les requêtes HTTP avec interceptors configurés.
 * Types utilisés :
 * - ICategoryBody : structure des données d'une catégorie.
 * - IApiResponseCategory : structure de la réponse de l'API pour les catégories.
 * - AxiosError : type pour gérer les erreurs des requêtes HTTP.
 *
 * Références externes :
 * - restaurantId : identifiant du restaurant courant.
 * - idCategory : identifiant de la catégorie sélectionnée.
 * - idToDelete : identifiant de la catégorie à supprimer.
 * - pageCategory : numéro de page pour la pagination.
 */
import { toRaw } from 'vue';
import type {
  IApiResponseCategory,
  ICategoryBody,
} from '@/types/category.types';
import type { AxiosError } from 'axios';
import axiosInstance from '@/services/interceptor.services';

import { restaurantId } from '@/refs/common.refs';
import {
  commerceId,
  idToDelete,
  organizationIdCommerce,
} from '@/refs/commerce.refs';
import { idCategory, pageCategory } from '@/refs/categories.refs';

export const CategoryService = {
  /**
   * Crée une nouvelle catégorie de produit.
   * @param data - Données de la catégorie à créer (ICategoryBody)
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  createCategory: async (data: ICategoryBody) => {
    try {
      const response = axiosInstance.post(`products/categories/create`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère la liste des catégories avec pagination pour le restaurant courant.
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  getCategory: async () => {
    try {
      const page = Number(toRaw(pageCategory.value));
      const response = await axiosInstance.get<IApiResponseCategory>(
        `products/categories/search?pagination=true&page=${page}&organizationIds=${organizationIdCommerce.value}`,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère TOUTES les catégories d'un commerce (non paginé côté API),
   * y compris celles qui n'ont encore aucun produit assigné.
   * @param storeId - Identifiant du commerce
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  getCategoriesByStore: async (storeId: string) => {
    try {
      const response = await axiosInstance.get<IApiResponseCategory>(
        `products/categories/search?pagination=false&storeIds=${storeId}&sortBy=position&sortOrder=ASC`,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère une catégorie spécifique par son identifiant.
   * @returns Promise contenant la réponse Axios ou l'erreur, ou undefined si idCategory est null
   */
  getCategoryById: async () => {
    try {
      if (idCategory.value === null) return;
      const id = toRaw(idCategory.value);
      const response = await axiosInstance.get<IApiResponseCategory>(
        `products/categories/get/${id}`,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Met à jour une catégorie existante par son identifiant.
   * @param categoryData - Données partielles à mettre à jour (Partial<ICategoryBody>)
   * @returns Promise contenant la réponse Axios ou l'erreur, ou undefined si idCategory est null
   */
  updateCategoryById: async (categoryData: Partial<ICategoryBody>) => {
    try {
      if (idCategory.value === null) return;
      const id = toRaw(idCategory.value);
      const response = await axiosInstance.put<any>(
        `products/categories/update/${id}`,
        categoryData,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Supprime une catégorie existante par son identifiant.
   * @returns Promise contenant la réponse Axios ou l'erreur, ou undefined si idToDelete est null
   */
  DeleteCategory: async () => {
    try {
      if (idToDelete.value === null) return;
      const id = toRaw(idToDelete.value);
      const response = axiosInstance.delete(
        `products/categories/delete/${id}`,
        {
          headers: { 'Content-Type': 'application/json' },
        },
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },
};
